import { initializeApp } from "firebase/app";
import { getFirestore, collection, doc, getDocs, getDoc, addDoc, setDoc, updateDoc, deleteDoc, query, orderBy, increment } from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  بيانات مشروع tamageed-gadeda
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const firebaseConfig = {
  apiKey: "AIzaSyBSbH9fsrHW7QPVOevmiGsC8L7LKBUD8a4",
  authDomain: "tamageed-gadeda.firebaseapp.com",
  projectId: "tamageed-gadeda",
  storageBucket: "tamageed-gadeda.firebasestorage.app",
  messagingSenderId: "455557121041",
  appId: "1:455557121041:web:f2ea3ddecf45189a0cddb0",
  measurementId: "G-2PKE0V28R6"
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  أسماء المجموعات في قاعدة البيانات الفعلية
//  Live database collection names
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const TAMGEEDS = "tamgeeds";     // (كان hymns في الكود الأصلي)
const CATEGORIES = "categories";

// ━━━━━━━━ TAMGEED ⇄ HYMN MAPPING ━━━━━━━━
// The dashboard UI works with a "hymn" shape ({ titleAr, verses[...] }).
// The database stores each tamgeed as { ar:{headers,lines}, en:{headers,lines}, ... }.
// These helpers translate between the two so the UI needs no changes.

function readLR(obj) {
  const right = (obj && Array.isArray(obj.right)) ? obj.right : ((obj && Array.isArray(obj.lines)) ? obj.lines : []);
  const left = (obj && Array.isArray(obj.left)) ? obj.left : [];
  return { right, left };
}

// القبطي والقبطي المعرّب عمود واحد بلا شطور
function readLines(obj) {
  if (obj && Array.isArray(obj.lines)) return obj.lines;
  if (obj && Array.isArray(obj.right)) return obj.right; // توافق مع البيانات القديمة
  return [];
}

function tamgeedToHymn(id, t) {
  const A = readLR(t.ar), E = readLR(t.en);
  const C = readLines(t.coptic), CA = readLines(t.coptic_arabic);
  const count = Math.max(A.right.length, A.left.length, E.right.length, E.left.length, C.length, CA.length, 1);
  const verses = [];
  for (let i = 0; i < count; i++) {
    verses.push({
      verseNumber: i + 1,
      arRight: A.right[i] || "", arLeft: A.left[i] || "",
      enRight: E.right[i] || "", enLeft: E.left[i] || "",
      coptic: C[i] || "",
      copticArabic: CA[i] || "",
    });
  }
  return {
    id,
    serialNumber: typeof t.order === "number" ? t.order : 0,
    titleAr: t.titleAr || "",
    titleEn: t.titleEn || "",
    icon: t.icon || "🎵",
    melodyAr: t.melodyAr || "", melodyEn: t.melodyEn || "",
    tuneName: t.tuneName || "",
    occasionAr: t.occasionAr || "", occasionEn: t.occasionEn || "",
    categoryId: t.categoryId || "",
    collectionId: t.collectionId || "",
    youtubeUrl: t.youtube || t.youtubeUrl || t.youtube_url || "",
    status: t.status || "draft",
    isFeatured: t.isFeatured || false,
    order: typeof t.order === "number" ? t.order : 0,
    verses
  };
}

function versesToHemistiches(verses) {
  const g = (k) => (verses || []).map((v) => v[k] || "");
  return {
    ar: { right: g("arRight"), left: g("arLeft") },
    en: { right: g("enRight"), left: g("enLeft") },
    coptic: g("coptic"),           // عمود واحد
    coptic_arabic: g("copticArabic"), // عمود واحد
  };
}

// ━━━━━━━━━━━━━━━━ TAMGEEDS API ━━━━━━━━━━━━━━━━
// (keeps the original "Hymn" function names so Dashboard.js is unchanged)

export async function getHymns() {
  const q = query(collection(db, TAMGEEDS), orderBy("order"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => tamgeedToHymn(d.id, d.data()));
}

export async function addHymn(data) {
  const h = versesToHemistiches(data.verses);
  const docRef = await addDoc(collection(db, TAMGEEDS), {
    titleAr: data.titleAr || "",
    titleEn: data.titleEn || "",
    icon: data.icon || "🎵",
    melodyAr: data.melodyAr || "",
    melodyEn: data.melodyEn || "",
    tuneName: data.tuneName || "",
    occasionAr: data.occasionAr || "",
    occasionEn: data.occasionEn || "",
    categoryId: data.categoryId || "",
    collectionId: data.collectionId || "",
    status: data.status || "draft",
    order: data.serialNumber || 0,
    ar: { headers: [], right: h.ar.right, left: h.ar.left },
    en: { headers: [], right: h.en.right, left: h.en.left },
    coptic: { lines: h.coptic },
    coptic_arabic: { lines: h.coptic_arabic },
    youtube: data.youtubeUrl || "",
    image: data.image || "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  await updateDoc(docRef, { id: docRef.id });
  await bumpDataVersion();
  return docRef.id;
}

export async function updateHymn(id, data) {
  const h = versesToHemistiches(data.verses);
  await updateDoc(doc(db, TAMGEEDS, id), {
    titleAr: data.titleAr || "",
    titleEn: data.titleEn || "",
    icon: data.icon || "🎵",
    melodyAr: data.melodyAr || "",
    melodyEn: data.melodyEn || "",
    tuneName: data.tuneName || "",
    occasionAr: data.occasionAr || "",
    occasionEn: data.occasionEn || "",
    categoryId: data.categoryId || "",
    status: data.status || "draft",
    order: data.serialNumber || 0,
    "ar.right": h.ar.right, "ar.left": h.ar.left,
    "en.right": h.en.right, "en.left": h.en.left,
    coptic: { lines: h.coptic },
    coptic_arabic: { lines: h.coptic_arabic },
    youtube: data.youtubeUrl || "",
    updatedAt: new Date().toISOString()
  });
  await bumpDataVersion();
}

export async function deleteHymn(id) {
  await deleteDoc(doc(db, TAMGEEDS, id));
  await bumpDataVersion();
}

// ━━━━━━━━━━━━━━ CATEGORIES API ━━━━━━━━━━━━━━
// Categories in the DB have no "order" field, so we sort client-side.

export async function getCategories() {
  const snap = await getDocs(collection(db, CATEGORIES));
  const cats = snap.docs.map((d) => {
    const c = d.data();
    return {
      id: d.id,
      nameAr: c.nameAr || "",
      nameEn: c.nameEn || "",
      icon: c.icon || "☩",
      iconType: "emoji",
      color: c.color || "#29ABE2",
      image: c.image || "",
      order: typeof c.order === "number" ? c.order : 0,
      parentId: c.parentId || ""
    };
  });
  cats.sort((a, b) => (a.order || 0) - (b.order || 0));
  return cats;
}

export async function addCategory(data) {
  const id = data.id;
  // setDoc (not updateDoc) so a brand-new category document can be created.
  await setDoc(
    doc(db, CATEGORIES, id),
    {
      id,
      nameAr: data.nameAr || "",
      nameEn: data.nameEn || "",
      icon: data.icon || "☩",
      color: data.color || "#29ABE2",
      image: data.image || "",
      order: typeof data.order === "number" ? data.order : 0,
      parentId: data.parentId || ""
    },
    { merge: true }
  );
  await bumpDataVersion();
}

export async function updateCategory(id, data) {
  await updateDoc(doc(db, CATEGORIES, id), {
    nameAr: data.nameAr || "",
    nameEn: data.nameEn || "",
    icon: data.icon || "☩",
    color: data.color || "#29ABE2",
    image: data.image || "",
    order: typeof data.order === "number" ? data.order : 0,
    parentId: data.parentId || ""
  });
  await bumpDataVersion();
}

export async function deleteCategory(id) {
  await deleteDoc(doc(db, CATEGORIES, id));
  await bumpDataVersion();
}

// ━━━━━━━━━━━━━━━━ STORAGE API ━━━━━━━━━━━━━━━
// NOTE: Firebase Storage requires the Blaze (pay-as-you-go) plan.
// The category editor uses emoji icons by default, so this is optional.

export async function uploadIcon(file, categoryId) {
  const ext = file.name.split(".").pop();
  const storageRef = ref(storage, `categories/${categoryId}/icon.${ext}`);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return await getDownloadURL(storageRef);
}

// ━━━━━━━━━━━━━━ DATA VERSION ━━━━━━━━━━━━━━━━
// Bumps settings/appVersion so the mobile app knows to refetch.
// lastUpdated is written as an ISO string to match the existing field type.

async function bumpDataVersion() {
  const versionRef = doc(db, "settings", "appVersion");
  const snap = await getDoc(versionRef);
  if (snap.exists()) {
    await updateDoc(versionRef, {
      version: increment(1),
      lastUpdated: new Date().toISOString()
    });
  } else {
    await setDoc(versionRef, {
      version: 1,
      lastUpdated: new Date().toISOString()
    });
  }
}

// ━━━━━━━━━━━━━━━━ AUTH API ━━━━━━━━━━━━━━━━━━

export async function loginAdmin(email, password) {
  return await signInWithEmailAndPassword(auth, email, password);
}

export async function logoutAdmin() {
  return await signOut(auth);
}

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}
